import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Overline, IMG } from "./shared";

const CHAPTERS = [
  {
    n: "01",
    axis: "Entretenir",
    title: "Entretien d'immeubles & PPE",
    text: "Conciergerie et entretien régulier des parties communes, contrôle visuel et suivi général de l'immeuble — notre service principal.",
    to: "/entretien-immeubles-ppe",
    tags: ["Halls & escaliers", "Zones poubelles", "Contrôle visuel"],
    img: "/assets/gallery/nettoyage-facade-nacelle-sion.webp",
  },
  {
    n: "02",
    axis: "Remettre en état",
    title: "Nettoyage professionnel",
    text: "Nettoyage régulier ou ponctuel, fin de bail, fin de chantier et interventions adaptées aux besoins particuliers.",
    to: "/nettoyage-professionnel",
    tags: ["Fin de bail", "Fin de chantier", "Sur mesure"],
    img: "/assets/gallery/sol-carrele-remise-a-neuf-sion.webp",
  },
  {
    n: "03",
    axis: "Remettre en état",
    title: "Nettoyages spéciaux & haute pression",
    text: "Redonner un coup de jeune aux surfaces extérieures et aux zones fortement sollicitées grâce à des techniques et équipements adaptés.",
    to: "/nettoyage-haute-pression",
    img: IMG.pression,
  },
  {
    n: "04",
    axis: "Intervenir",
    title: "Petits & moyens travaux d'entretien",
    text: "Petites réparations, maintenance, rafraîchissements et remise en état — l'avantage d'un interlocuteur polyvalent qui connaît votre bien.",
    to: "/travaux-entretien",
    img: IMG.travaux,
  },
];

export const Services = () => (
  <section id="services" className="bg-white py-24 lg:py-36" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-10">
      <Reveal>
        <Overline>Nos domaines d'intervention</Overline>
        <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium leading-tight tracking-tight text-nuit sm:text-5xl">
          Quatre métiers, un seul interlocuteur.
        </h2>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-12 grid grid-cols-1 gap-px bg-nuit/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="services-axes">
          {[
            { t: "Entretenir", d: "Conciergerie, PPE, parties communes, suivi du bien, contrôle des évacuations d'eau, collaboration avec les entreprises mandatées", icon: "/assets/icone-conciergerie.png" },
            { t: "Remettre en état", d: "Haute pression, fin de bail, fin de chantier, remise en état ponctuelle", icon: "/assets/icone-debarras.png" },
            { t: "Intervenir", d: "Petites réparations, maintenance, extérieurs, murs en pierre et en béton, garages souterrains", icon: "/assets/icone-travaux.png" },
            { t: "Transporter & débarrasser", d: "Débarras, évacuation, transport avec véhicules et remorques", icon: "/assets/icone-transport.png" },
          ].map((a, i) => (
            <div key={a.t} className="bg-white p-6 lg:p-7">
              <div className="flex items-center gap-4">
                <img src={a.icon} alt="" aria-hidden="true" loading="lazy" className="h-12 w-12 shrink-0" />
                <p className="font-display text-lg font-medium text-nuit">{a.t}</p>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-nuit/55">{a.d}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <div className="mt-16 lg:mt-24">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={0.05}>
            <Link
              to={c.to}
              data-testid={`service-chapter-${c.n}`}
              className="group grid grid-cols-1 items-center gap-6 border-t border-nuit/10 py-10 transition-colors duration-500 hover:bg-brume/60 md:grid-cols-12 md:gap-8 lg:py-14"
            >
              <div className="md:col-span-3 lg:col-span-2">
                <span className="font-display text-stroke-nuit text-7xl font-bold leading-none transition-all duration-500 group-hover:text-lagoon group-hover:[-webkit-text-stroke:0px] lg:text-[7rem]">
                  {c.n}
                </span>
              </div>
              <div className="md:col-span-6 lg:col-span-6">
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-lagoon">{c.axis}</span>
                <h3 className="font-display mt-1.5 text-xl font-medium text-nuit sm:text-2xl lg:text-3xl">{c.title}</h3>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-nuit/60 sm:text-base">{c.text}</p>
                {c.tags && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {c.tags.map((t) => (
                      <span key={t} className="border border-nuit/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-petrole">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="hidden md:col-span-3 lg:col-span-4 md:block">
                {c.img ? (
                  <div className="overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.title}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-end">
                    <ArrowUpRight className="h-10 w-10 text-nuit/20 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-lagoon" />
                  </div>
                )}
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
